import React, { ReactNode, useCallback, useReducer } from 'react';
import { SpoolDto } from '../types/spool-local';
import { ALL_ASSETS, ALL_RISK_MODELS, SortDirection } from '../../config/constants';
import AvailableSpoolContext from './spoolsContext';
import spoolsReducer from './spoolsReducer';
import { ActiveFilterType, ActiveSortType, SortKey } from './spoolsType';
import { isUserSpoolKey } from '../../helpers/sdkHelper';

type Props = {
    children: ReactNode;
    initialSortKey: SortKey;
};

const SpoolsState: React.FC<Props> = ({ children, initialSortKey }) => {
    const setSearchTerm = useCallback(
        (term: string): void =>
            dispatch({
                type: 'SET_SEARCH_TERM',
                payload: term,
            }),
        []
    );

    const setActiveFilter = useCallback(
        (filterKey: string, value: string | boolean): void =>
            dispatch({
                type: 'SET_ACTIVE_FILTER',
                payload: {
                    key: filterKey,
                    value,
                },
            }),
        []
    );

    const sort = useCallback((a: SpoolDto, b: SpoolDto, activeSort: ActiveSortType) => {
        if (!activeSort?.key) return 0;

        const direction = activeSort.direction === SortDirection.ASC ? 1 : -1;

        const aValue = isUserSpoolKey(activeSort.key)
            ? parseFloat(a.userData![activeSort.key]!.toString())
            : parseFloat(a[activeSort.key]!.toString());
        const bValue = isUserSpoolKey(activeSort.key)
            ? parseFloat(b.userData![activeSort.key]!.toString())
            : parseFloat(b[activeSort.key]!.toString());

        return (aValue - bValue) * direction;
    }, []);

    const handleSortClick = useCallback((key: SortKey, activeSort: ActiveSortType) => {
        if (!activeSort?.key || activeSort?.key !== key) {
            dispatch({
                type: 'SET_ACTIVE_SORT',
                payload: {
                    key,
                    direction: SortDirection.DESC,
                },
            });
            return;
        }

        dispatch({
            type: 'CHANGE_ACTIVE_SORT_DIRECTION',
        });
    }, []);

    const filterData = useCallback(
        (data: SpoolDto, activeFilter: ActiveFilterType, searchTerm: string) => {
            const filteredAsset = activeFilter.asset;
            const filteredModel = activeFilter.riskModelAddress;

            const spoolNameFilter = searchTerm
                ? data.name.toUpperCase().includes(searchTerm.toUpperCase())
                : true;

            const addressFilter = searchTerm
                ? data.address.toUpperCase().includes(searchTerm.toUpperCase())
                : true;

            const searchResults = spoolNameFilter || addressFilter;

            // Showing incentivzed only, check if rewardsTokens exist, if not return false
            if (activeFilter.incentivized && !data.incentivizedRewards?.length)
                return false;

            if (filteredAsset === ALL_ASSETS && filteredModel === ALL_RISK_MODELS) {
                return searchResults;
            }

            if (filteredAsset === ALL_ASSETS) {
                return filteredModel === data.riskModelAddress && searchResults;
            }

            if (filteredModel === ALL_RISK_MODELS) {
                return filteredAsset === data.asset.symbol && searchResults;
            }

            return (
                filteredModel === data.riskModelAddress &&
                filteredAsset === data.asset.symbol &&
                searchResults
            );
        },
        []
    );

    const toggleFilter = useCallback(
        (filterKey: string): void =>
            dispatch({
                type: 'TOGGLE_FILTER_SHOWN',
                payload: filterKey,
            }),
        []
    );

    const setSelectDropdown = useCallback(
        (dropdownKey: string, dropdownState: boolean): void =>
            dispatch({
                type: 'SET_SELECT_DROPDOWN',
                payload: {
                    key: dropdownKey,
                    value: dropdownState,
                },
            }),
        []
    );

    const initialState = {
        searchTerm: '',
        activeFilter: {
            riskModelAddress: ALL_RISK_MODELS,
            asset: ALL_ASSETS,
            incentivized: false,
        },
        showSelect: {
            riskModelAddress: false,
            asset: false,
        },
        showSelectDropdown: {
            riskModelAddress: false,
            asset: false,
        },
        activeSort: {
            key: initialSortKey,
            direction: SortDirection.DESC,
        },
        sort,
        handleSortClick,
        filterData,
        setSearchTerm,
        setActiveFilter,
        toggleFilter,
        setSelectDropdown,
    };

    const [state, dispatch] = useReducer(spoolsReducer, initialState);

    return (
        <AvailableSpoolContext.Provider
            value={{
                searchTerm: state.searchTerm,
                activeFilter: state.activeFilter,
                showSelect: state.showSelect,
                showSelectDropdown: state.showSelectDropdown,
                activeSort: state.activeSort,
                sort,
                handleSortClick,
                filterData,
                setSearchTerm,
                setActiveFilter,
                toggleFilter,
                setSelectDropdown,
            }}
        >
            {children}
        </AvailableSpoolContext.Provider>
    );
};

export default SpoolsState;
